import type { HomeBootstrap } from "@/api/content";
import type { FanFolder } from "@/types/content";

export interface CreatorOnboardingProgress {
  hasWelcomeVideo: boolean;
  hasFirstOffer: boolean;
  hasPricing: boolean;
  isComplete: boolean;
}

function parsePriceAmount(value: string | null | undefined): number {
  if (!value) {
    return 0;
  }

  const cleaned = value.replace(",", ".").replace(/[^0-9.]/g, "");
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
}

function flattenFolders(folders: FanFolder[]): FanFolder[] {
  return folders.flatMap((folder) => [folder, ...flattenFolders(folder.subFolders ?? [])]);
}

export function getCreatorOnboardingProgress(bootstrap: HomeBootstrap | null | undefined): CreatorOnboardingProgress {
  const posts = bootstrap?.posts?.data ?? [];
  const memberships = bootstrap?.memberships ?? [];
  const oneTimePurchaseFolders = flattenFolders(bootstrap?.folders ?? []).filter((folder) => Boolean(folder.oneTimePurchase?.enabled));

  const hasWelcomeVideo = posts.some((post) => {
    const type = String(post.type ?? "").toLowerCase();
    const hasVideoMedia = post.media.some((media) => String(media.type ?? "").toLowerCase().includes("video"));
    return (type === "video" || hasVideoMedia) && post.isPublished !== 0;
  });

  const activeMemberships = memberships.filter((membership) => membership.isActive !== 0);
  const hasFirstOffer = activeMemberships.length > 0 || oneTimePurchaseFolders.length > 0;
  const hasPricedMembership = activeMemberships.some((membership) => {
    const cashierProductId = String(membership.cashierProductId ?? "").trim().toLowerCase();
    return cashierProductId === "free" || parsePriceAmount(membership.price) > 0 || Boolean(cashierProductId);
  });
  const hasOneTimePurchase = oneTimePurchaseFolders.some((folder) => Boolean(folder.oneTimePurchase?.enabled));
  const hasPricing = hasPricedMembership || hasOneTimePurchase;
  const isComplete = hasWelcomeVideo && hasFirstOffer && hasPricing;

  return {
    hasWelcomeVideo,
    hasFirstOffer,
    hasPricing,
    isComplete,
  };
}
